import React, { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import { Button, Checkbox, Select, Input, message, Tooltip, Table, Modal, Spin, Drawer } from "antd";
import { 
  ChevronRight, 
  Trash2, 
  Play, 
  Download, 
  RefreshCw, 
  Plus, 
  ArrowLeftRight,
  Database,
  ArrowLeft,
  X,
  PlusCircle,
  Filter
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NodeReportQuery_api } from "../../Api/Reports/node_report_query";
import { 
  SaveReportTemplate_api, 
  FetchReportTemplates_api, 
  DeleteReportTemplate_api,
  UpdateReportTemplate_api 
} from "../../Api/Reports/custom_report_query";
import { useAuthChecker } from "../../Context";
import { downloadExcel } from "../../lib/excelGenerator";
import { Save, FolderOpen, PieChart, Calculator, Edit3, Bot, Sparkles, Send, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { CallAiAgent_api } from "../../Api";
import TextArea from "antd/es/input/TextArea";

const { Option } = Select;

const CARD_WIDTH = 260;
const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 56;

// Table Node Schema Definitions
export const TABLE_NODES_CONFIG: Record<string, { label: string, fields: { name: string, type: 'text' | 'number' | 'date' | 'datetime' }[] }> = {
  payment_details: {
    label: "Payments",
    fields: [
      { name: "id", type: "number" },
      { name: "slip_id", type: "text" },
      { name: "slip_date", type: "date" },
      { name: "amount", type: "number" },
      { name: "customer_id", type: "number" },
      { name: "payment_type_id", type: "number" },
      { name: "added_user", type: "number" },
      { name: "added_date_time", type: "datetime" },
      { name: "dataobj", type: "text" },
      { name: "updated_user", type: "number" },
      { name: "updated_date_time", type: "datetime" },
      { name: "imageName", type: "text" },
      { name: "is_credited", type: "number" }
    ]
  },
  customer: {
    label: "Customers",
    fields: [
      { name: "id", type: "number" },
      { name: "name", type: "text" },
      { name: "mobile", type: "text" }
    ]
  },
  inquiry_number: {
    label: "Inquiries",
    fields: [
      { name: "id", type: "number" },
      { name: "inquiryNumber", type: "text" },
      { name: "totalamount", type: "number" },
      { name: "amount", type: "number" },
      { name: "discount", type: "number" },
      { name: "status", type: "text" },
      { name: "isPaidCompany", type: "number" },
      { name: "company_paid_amount", type: "number" },
      { name: "vehicle_type_id", type: "number" },
      { name: "vehicle_modal_id", type: "number" },
      { name: "vehicle_id", type: "number" },
      { name: "priceList", type: "text" },
      { name: "dealer_id", type: "number" },
      { name: "sales_person_id", type: "number" },
      { name: "isOfferAdded", type: "number" },
      { name: "downpayment", type: "number" },
      { name: "loanamount", type: "number" }
    ]
  },
  vehicle: {
    label: "Vehicles",
    fields: [
      { name: "id", type: "number" },
      { name: "engine_number", type: "text" },
      { name: "chassis_number", type: "text" },
      { name: "status", type: "text" },
      { name: "dealer_id", type: "number" },
      { name: "current_dealer_id", type: "number" },
      { name: "v_color", type: "number" },
      { name: "vehicle_type_has_vehicle_modal_id", type: "number" },
      { name: "return_date", type: "datetime" },
      { name: "reaturn_reson", type: "text" },
      { name: "added_date", type: "datetime" },
      { name: "sold_date", type: "datetime" },
      { name: "sold_remarks", type: "text" },
      { name: "gate_pass_image", type: "text" },
      { name: "instock_date", type: "datetime" }
    ]
  },
  dealers: {
    label: "Dealers",
    fields: [
      { name: "id", type: "number" },
      { name: "name", type: "text" },
      { name: "contact_number", type: "text" },
      { name: "email", type: "text" },
      { name: "city_id", type: "number" },
      { name: "is_active", type: "number" }
    ]
  },
  sales_person: {
    label: "Sales Persons",
    fields: [
      { name: "id", type: "number" },
      { name: "name", type: "text" },
      { name: "contact_number", type: "text" },
      { name: "email", type: "text" },
      { name: "status", type: "number" },
      { name: "dealer_id", type: "number" }
    ]
  },
  remittance: {
    label: "Remittances",
    fields: [
      { name: "id", type: "number" },
      { name: "transfer_id", type: "text" },
      { name: "slip_date", type: "date" },
      { name: "amount", type: "number" },
      { name: "bank_account_id", type: "number" },
      { name: "bank_accounts_id", type: "number" },
      { name: "user_id", type: "number" },
      { name: "dataObj", type: "text" },
      { name: "added_date_time", type: "datetime" },
      { name: "updated_user", type: "number" },
      { name: "updated_date_time", type: "datetime" },
      { name: "imageName", type: "text" }
    ]
  },
  bank_account: {
    label: "Bank Accounts",
    fields: [
      { name: "id", type: "number" },
      { name: "bank_name", type: "text" },
      { name: "account_number", type: "text" }
    ]
  },
  bank_transaction: {
    label: "Bank Transactions",
    fields: [
      { name: "id", type: "number" },
      { name: "date", type: "datetime" },
      { name: "amount", type: "number" },
      { name: "from_bank_id", type: "number" },
      { name: "to_bank_id", type: "number" },
      { name: "payload", type: "text" },
      { name: "addedDate", type: "datetime" },
      { name: "user_id", type: "number" },
      { name: "transactionfee", type: "number" },
      { name: "transaction_type", type: "text" },
      { name: "other_details", type: "text" }
    ]
  },
  stock_transfer: {
    label: "Stock Transfers",
    fields: [
      { name: "id", type: "number" },
      { name: "vehicle_id", type: "number" },
      { name: "from_stock_type", type: "text" },
      { name: "from_dealer_id", type: "number" },
      { name: "to_stock_type", type: "text" },
      { name: "to_dealer_id", type: "number" },
      { name: "transfer_date", type: "datetime" },
      { name: "remarks", type: "text" }
    ]
  },
  grn: {
    label: "GRN Purchases",
    fields: [
      { name: "id", type: "number" },
      { name: "invoice_id", type: "text" },
      { name: "invoice_date", type: "date" },
      { name: "total", type: "number" },
      { name: "added_date", type: "datetime" }
    ]
  },
  credit: {
    label: "Credits",
    fields: [
      { name: "id", type: "number" },
      { name: "amount", type: "number" },
      { name: "added_date", type: "date" },
      { name: "status", type: "number" },
      { name: "paid_date", type: "date" },
      { name: "inquiry_number", type: "number" },
      { name: "payment_details_id", type: "number" }
    ]
  },
  product_has_stock: {
    label: "Inventory Stock",
    fields: [
      { name: "id", type: "number" },
      { name: "product_id", type: "number" },
      { name: "qty", type: "number" },
      { name: "cost", type: "number" },
      { name: "status", type: "number" }
    ]
  },
  product_release: {
    label: "Inventory Releases",
    fields: [
      { name: "id", type: "number" },
      { name: "inquery_number", type: "number" },
      { name: "qty", type: "number" },
      { name: "product_has_stock", type: "number" },
      { name: "date", type: "date" },
      { name: "product_name", type: "text" }
    ]
  },
  customer_has_inquiry_number: {
    label: "Customer Inquiry Map",
    fields: [
      { name: "customer_id", type: "number" },
      { name: "inquiry_number_id", type: "number" }
    ]
  },
  payment_has_inquiry_number: {
    label: "Payment Inquiry Map",
    fields: [
      { name: "payment_id", type: "number" },
      { name: "inquiry_number_id", type: "number" }
    ]
  },
  vehicle_type_has_vehicle_modal: {
    label: "Vehicle Type-Model Map",
    fields: [
      { name: "id", type: "number" },
      { name: "vehicle_type_id", type: "number" },
      { name: "vehicle_modal_id", type: "number" }
    ]
  },
  vehicle_type: {
    label: "Vehicle Types",
    fields: [
      { name: "id", type: "number" },
      { name: "v_type", type: "text" }
    ]
  },
  vehicle_modal: {
    label: "Vehicle Models",
    fields: [
      { name: "id", type: "number" },
      { name: "v_modal", type: "text" }
    ]
  },
  remittance_has_inquiry_number: {
    label: "Remittance Inquiry Map",
    fields: [
      { name: "remittance_id", type: "number" },
      { name: "inquiry_number_id", type: "number" }
    ]
  },
  dealer_payment: {
    label: "Dealer Payments",
    fields: [
      { name: "id", type: "number" },
      { name: "dealer_id", type: "number" },
      { name: "amount", type: "number" },
      { name: "date", type: "date" }
    ]
  },
  dealer_payment_has_vehicle: {
    label: "Dealer Payment Vehicle Map",
    fields: [
      { name: "dealer_payment_id", type: "number" },
      { name: "vehicle_id", type: "number" }
    ]
  },
  payment_type: {
    label: "Payment Types",
    fields: [
      { name: "id", type: "number" },
      { name: "type", type: "text" }
    ]
  },
  city: {
    label: "Cities",
    fields: [
      { name: "id", type: "number" },
      { name: "city", type: "text" }
    ]
  },
  vehicle_color: {
    label: "Vehicle Colors",
    fields: [
      { name: "id", type: "number" },
      { name: "color", type: "text" }
    ]
  },
  product: {
    label: "Products",
    fields: [
      { name: "id", type: "number" },
      { name: "name", type: "text" },
      { name: "status", type: "number" }
    ]
  },
  grn_item: {
    label: "GRN Items",
    fields: [
      { name: "id", type: "number" },
      { name: "grn_id", type: "number" },
      { name: "product_has_stock_id", type: "number" }
    ]
  }
};

export const RELATION_MAP = [
  { fromTable: "customer", fromField: "id", toTable: "customer_has_inquiry_number", toField: "customer_id", description: "Links Customer directly to Customer Inquiry Map" },
  { fromTable: "inquiry_number", fromField: "id", toTable: "customer_has_inquiry_number", toField: "inquiry_number_id", description: "Links Inquiry Number to Customer Inquiry Map" },
  { fromTable: "payment_details", fromField: "id", toTable: "payment_has_inquiry_number", toField: "payment_id", description: "Links Customer Payments to Payment Inquiry Map" },
  { fromTable: "inquiry_number", fromField: "id", toTable: "payment_has_inquiry_number", toField: "inquiry_number_id", description: "Links Inquiry Number to Payment Inquiry Map" },
  { fromTable: "remittance", fromField: "id", toTable: "remittance_has_inquiry_number", toField: "remittance_id", description: "Links Remittance to Remittance Inquiry Map" },
  { fromTable: "inquiry_number", fromField: "id", toTable: "remittance_has_inquiry_number", toField: "inquiry_number_id", description: "Links Inquiry Number to Remittance Inquiry Map" },
  { fromTable: "inquiry_number", fromField: "dealer_id", toTable: "dealers", toField: "id", description: "Links Inquiry Number to Dealer" },
  { fromTable: "inquiry_number", fromField: "sales_person_id", toTable: "sales_person", toField: "id", description: "Links Inquiry Number to Sales Person" },
  { fromTable: "inquiry_number", fromField: "vehicle_modal_id", toTable: "vehicle_modal", toField: "id", description: "Links Inquiry to Vehicle Model catalog" },
  { fromTable: "inquiry_number", fromField: "vehicle_type_id", toTable: "vehicle_type", toField: "id", description: "Links Inquiry to Vehicle Type catalog" },
  { fromTable: "vehicle", fromField: "vehicle_type_has_vehicle_modal_id", toTable: "vehicle_type_has_vehicle_modal", toField: "id", description: "Links Vehicle to Model-Type Map" },
  { fromTable: "vehicle_type_has_vehicle_modal", fromField: "vehicle_modal_id", toTable: "vehicle_modal", toField: "id", description: "Links Model-Type Map to Vehicle Model catalog" },
  { fromTable: "vehicle_type_has_vehicle_modal", fromField: "vehicle_type_id", toTable: "vehicle_type", toField: "id", description: "Links Model-Type Map to Vehicle Type catalog" },
  { fromTable: "vehicle", fromField: "dealer_id", toTable: "dealers", toField: "id", description: "Links Vehicle to Dealer" },
  { fromTable: "dealers", fromField: "city_id", toTable: "city", toField: "id", description: "Links Dealer to Location City" },
  { fromTable: "dealer_payment", fromField: "dealer_id", toTable: "dealers", toField: "id", description: "Links Dealer Payment directly to Dealer" },
  { fromTable: "dealer_payment_has_vehicle", fromField: "dealer_payment_id", toTable: "dealer_payment", toField: "id", description: "Links Payment Vehicle Map to Dealer Payment record" },
  { fromTable: "dealer_payment_has_vehicle", fromField: "vehicle_id", toTable: "vehicle", toField: "id", description: "Links Payment Vehicle Map to Vehicle" },
  { fromTable: "payment_details", fromField: "customer_id", toTable: "customer", toField: "id", description: "Links Payment Details to Customer" },
  { fromTable: "payment_details", fromField: "payment_type_id", toTable: "payment_type", toField: "id", description: "Links Payment Details to Payment Type" },
  { fromTable: "product_has_stock", fromField: "product_id", toTable: "product", toField: "id", description: "Links Product Stock to Catalog Product details" },
  { fromTable: "product_release", fromField: "product_has_stock", toTable: "product_has_stock", toField: "id", description: "Links Inventory Release to Product Stock" },
  { fromTable: "product_release", fromField: "inquery_number", toTable: "inquiry_number", toField: "id", description: "Links Inventory Release to Inquiry details" },
  { fromTable: "grn_item", fromField: "grn_id", toTable: "grn", toField: "id", description: "Links GRN Item invoice to GRN receipt" },
  { fromTable: "grn_item", fromField: "product_has_stock_id", toTable: "product_has_stock", toField: "id", description: "Links GRN Item directly to Stock Record" },
  { fromTable: "vehicle", fromField: "v_color", toTable: "vehicle_color", toField: "id", description: "Links Vehicle to Vehicle Color catalog" }
];

export const NodeReports = () => {
  const navigate = useNavigate();
  const workspaceRef = useRef<HTMLDivElement>(null);

  // AI Agent States
  const [isAiDrawerOpen, setIsAiDrawerOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");
  const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'model', text: string }[]>([]);

  // Zoom State
  const [zoom, setZoom] = useState<number>(1.0);

  const handleAiDesign = async (text: string) => {
    if (!text.trim()) return;
    setAiLoading(true);
    setAiExplanation("");
    
    const userMessage = { role: 'user' as const, text: text };
    const updatedHistory = [...chatHistory, userMessage];
    setChatHistory(updatedHistory);
    setAiPrompt("");

    const currentWorkspace = {
      nodes: activeNodes,
      connections: connections,
      columns: selectedColumns,
      filters: filters.map(f => ({ field: f.field, operator: f.operator, value: f.value })),
      limit: limit,
      isGrouping,
      groupByField,
      aggField,
      aggFunc,
      nodePositions
    };

    try {
      const res = await CallAiAgent_api(
        text + "\n[Context: Visual Workspace Builder. You MUST generate 'workspace' mode response with visualConfig. Modify/update the currentWorkspace provided in the prompt, merging the changes into it, and return the complete updated visualConfig.]",
        updatedHistory,
        currentWorkspace
      );
      if (res.success) {
        setAiExplanation(res.explanation || "");
        const aiResponseText = res.explanation || "I have updated the visual workspace according to your request.";
        setChatHistory(prev => [...prev, { role: 'model', text: aiResponseText }]);

        if (res.mode === "workspace" && res.visualConfig) {
          const config = res.visualConfig;
          if (config.nodes) setActiveNodes(config.nodes);
          if (config.connections) setConnections(config.connections);
          if (config.columns) setSelectedColumns(config.columns);
          if (config.filters) {
            const formattedFilters = config.filters.map((f: any, idx: number) => ({
              id: f.id || `filter-${idx}-${Date.now()}`,
              field: f.field,
              operator: f.operator,
              value: f.value
            }));
            setFilters(formattedFilters);
          }
          if (config.limit) setLimit(config.limit);
          
          // Set dynamic node positions
          if (config.nodes) {
            const posUpdates: Record<string, { x: number, y: number }> = {};
            config.nodes.forEach((node: string, idx: number) => {
              posUpdates[node] = nodePositions[node] || {
                x: 320 + idx * 60,
                y: 80 + idx * 50
              };
            });
            setNodePositions(posUpdates);
          }

          if (config.isGrouping !== undefined) setIsGrouping(config.isGrouping);
          if (config.groupByField) setGroupByField(config.groupByField);
          if (config.aggField) setAggField(config.aggField);
          if (config.aggFunc) setAggFunc(config.aggFunc);

          message.success("Successfully updated visual workspace layout!");
        } else {
          message.warning("AI executed a SQL query instead of designing a visual workspace. Use the Custom Reports page for direct SQL queries.");
        }
      } else {
        message.error(res.msg || "Failed to generate AI workspace");
        setChatHistory(prev => [...prev, { role: 'model', text: `Error: ${res.msg || "Failed to connect"}` }]);
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to connect to AI designer agent");
      setChatHistory(prev => [...prev, { role: 'model', text: "Error: Failed to connect to AI designer agent" }]);
    } finally {
      setAiLoading(false);
    }
  };

  // ERD helper states
  const [isErdOpen, setIsErdOpen] = useState(false);
  const [erdSearch, setErdSearch] = useState("");

  // Workspace configuration state
  const [activeNodes, setActiveNodes] = useState<string[]>([]);
  const [nodePositions, setNodePositions] = useState<Record<string, { x: number, y: number }>>({});
  const [connections, setConnections] = useState<{ fromTable: string, fromField: string, toTable: string, toField: string }[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  
  // Dynamic filter state
  const [filters, setFilters] = useState<{ id: string, field: string, operator: string, value: string }[]>([]);
  const [limit, setLimit] = useState<number>(100);

  // Execution & loading state
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [sqlDebug, setSqlDebug] = useState<string>("");

  // Connection dragging helpers
  const [linkingStart, setLinkingStart] = useState<{ table: string, field: string } | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number, y: number }>({ x: 0, y: 0 });

  // Sidebar search
  const [searchTableQuery, setSearchTableQuery] = useState("");

  const { user } = useAuthChecker();
  
  // Template States
  const [templates, setTemplates] = useState<any[]>([]);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadedTemplateId, setLoadedTemplateId] = useState<number | null>(null);
  const [loadedTemplateName, setLoadedTemplateName] = useState<string>("");

  // Aggregation States
  const [isGrouping, setIsGrouping] = useState<boolean>(false);
  const [groupByField, setGroupByField] = useState<string>("");
  const [aggField, setAggField] = useState<string>("");
  const [aggFunc, setAggFunc] = useState<string>("COUNT");

  // Load saved templates
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    const res = await FetchReportTemplates_api();
    if (res.success) {
      const nodeTemplates = (res.data || []).filter((t: any) => t.module === "node_workspace");
      setTemplates(nodeTemplates);
    }
    setLoadingTemplates(false);
  };

  useEffect(() => {
    loadTemplates();

    const tempWorkspace = localStorage.getItem("ai_temp_workspace");
    if (tempWorkspace) {
      try {
        const config = JSON.parse(tempWorkspace);
        if (config.nodes) setActiveNodes(config.nodes);
        if (config.connections) setConnections(config.connections);
        if (config.columns) setSelectedColumns(config.columns);
        if (config.filters) {
          const formattedFilters = config.filters.map((f: any, idx: number) => ({
            id: f.id || `filter-${idx}-${Date.now()}`,
            field: f.field,
            operator: f.operator,
            value: f.value
          }));
          setFilters(formattedFilters);
        }
        if (config.limit) setLimit(config.limit);
        
        // Set dynamic node positions
        if (config.nodes) {
          const posUpdates: Record<string, { x: number, y: number }> = {};
          config.nodes.forEach((node: string, idx: number) => {
            posUpdates[node] = {
              x: 320 + idx * 60,
              y: 80 + idx * 50
            };
          });
          setNodePositions(posUpdates);
        }

        if (config.isGrouping !== undefined) setIsGrouping(config.isGrouping);
        if (config.groupByField) setGroupByField(config.groupByField);
        if (config.aggField) setAggField(config.aggField);
        if (config.aggFunc) setAggFunc(config.aggFunc);

        message.success("Successfully loaded workspace built by AI Agent!");
      } catch (e) {
        console.error("Error loading temp workspace from AI:", e);
      } finally {
        localStorage.removeItem("ai_temp_workspace");
      }
    }
  }, []);

  // Save template
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      message.error("Please enter a template name");
      return;
    }
    const configuration = {
      nodes: activeNodes,
      connections,
      columns: selectedColumns,
      filters,
      limit,
      // Aggregations
      isGrouping,
      groupByField,
      aggField,
      aggFunc,
      // Node positions
      nodePositions,
    };
    const res = await SaveReportTemplate_api(templateName, "node_workspace", configuration, user?.userId || null, chatHistory);
    if (res.success) {
      message.success("Template saved successfully");
      setIsSaveModalOpen(false);
      setTemplateName("");
      loadTemplates();
    } else {
      message.error(res.msg || "Failed to save template");
    }
  };

  // Delete template
  const handleDeleteTemplate = async (e: React.MouseEvent, templateId: number) => {
    e.stopPropagation();
    const res = await DeleteReportTemplate_api(templateId);
    if (res.success) {
      message.success("Template deleted successfully");
      loadTemplates();
    } else {
      message.error(res.msg || "Failed to delete template");
    }
  };

  // Load template
  const handleLoadTemplate = (template: any) => {
    let config: any = {};
    try {
      config = typeof template.configuration === "string" ? JSON.parse(template.configuration) : template.configuration;
    } catch (e) {
      console.error(e);
    }
    if (config.nodes) setActiveNodes(config.nodes);
    if (config.connections) setConnections(config.connections);
    if (config.columns) setSelectedColumns(config.columns);
    if (config.filters) setFilters(config.filters);
    if (config.limit) setLimit(config.limit);
    if (config.nodePositions) setNodePositions(config.nodePositions);
    
    // Grouping
    if (config.isGrouping !== undefined) setIsGrouping(config.isGrouping);
    if (config.groupByField) setGroupByField(config.groupByField);
    if (config.aggField) setAggField(config.aggField);
    if (config.aggFunc) setAggFunc(config.aggFunc);

    // Chat History
    if (template.chat_history) {
      try {
        const history = typeof template.chat_history === 'string' ? JSON.parse(template.chat_history) : template.chat_history;
        setChatHistory(history || []);
      } catch (e) {
        console.error("Error loading chat history from template:", e);
        setChatHistory([]);
      }
    } else {
      setChatHistory([]);
    }

    setLoadedTemplateId(template.id);
    setLoadedTemplateName(template.name);
    message.success(`Loaded visual template: ${template.name}`);
  };

  const handleUpdateTemplate = async () => {
    if (!loadedTemplateId) {
      message.error("No workspace loaded to update");
      return;
    }
    const configuration = {
      nodes: activeNodes,
      connections,
      columns: selectedColumns,
      filters,
      limit,
      isGrouping,
      groupByField,
      aggField,
      aggFunc,
      nodePositions,
    };
    const res = await UpdateReportTemplate_api(loadedTemplateId, loadedTemplateName, "node_workspace", configuration, chatHistory);
    if (res.success) {
      message.success("Workspace updated successfully");
      loadTemplates();
    } else {
      message.error(res.msg || "Failed to update workspace");
    }
  };

  const autoJoinRelation = (relation: typeof RELATION_MAP[0]) => {
    let updatedNodes = [...activeNodes];
    let posUpdates = { ...nodePositions };
    let hasNode1 = activeNodes.includes(relation.fromTable);
    let hasNode2 = activeNodes.includes(relation.toTable);

    if (!hasNode1) {
      updatedNodes.push(relation.fromTable);
      posUpdates[relation.fromTable] = { x: 300 + updatedNodes.length * 35, y: 100 + updatedNodes.length * 30 };
    }
    if (!hasNode2) {
      updatedNodes.push(relation.toTable);
      posUpdates[relation.toTable] = { x: 480 + updatedNodes.length * 35, y: 140 + updatedNodes.length * 30 };
    }

    const connectionExists = connections.some(
      c =>
        (c.fromTable === relation.fromTable &&
          c.fromField === relation.fromField &&
          c.toTable === relation.toTable &&
          c.toField === relation.toField) ||
        (c.fromTable === relation.toTable &&
          c.fromField === relation.toField &&
          c.toTable === relation.fromTable &&
          c.toField === relation.fromField)
    );

    if (!connectionExists) {
      setConnections(prev => [
        ...prev,
        {
          fromTable: relation.fromTable,
          fromField: relation.fromField,
          toTable: relation.toTable,
          toField: relation.toField
        }
      ]);
    }

    setActiveNodes(updatedNodes);
    setNodePositions(posUpdates);
    message.success(`Mapped relationship: ${relation.fromTable}.${relation.fromField} ↔ ${relation.toTable}.${relation.toField}`);
  };

  const addNode = (tableKey: string) => {
    if (activeNodes.includes(tableKey)) {
      message.warning("Table node is already added to the canvas");
      return;
    }
    const id = activeNodes.length;
    // Spawn nodes in a diagonal cascade inside visible canvas
    const x = 320 + id * 50;
    const y = 80 + id * 40;

    setActiveNodes([...activeNodes, tableKey]);
    setNodePositions(prev => ({ ...prev, [tableKey]: { x, y } }));
  };

  const removeNode = (tableKey: string) => {
    setActiveNodes(activeNodes.filter(n => n !== tableKey));
    // Clear connections involving this table
    setConnections(connections.filter(c => c.fromTable !== tableKey && c.toTable !== tableKey));
    // Clear selected columns from this table
    setSelectedColumns(selectedColumns.filter(col => !col.startsWith(`${tableKey}.`)));
    // Clear filters involving this table
    setFilters(filters.filter(f => !f.field.startsWith(`${tableKey}.`)));
  };

  // Get coordinates for connection port
  const getPortCoords = (table: string, fieldName: string, side: 'left' | 'right') => {
    const pos = nodePositions[table];
    if (!pos) return { x: 0, y: 0 };

    const schema = TABLE_NODES_CONFIG[table];
    const fieldIndex = schema.fields.findIndex(f => f.name === fieldName);

    const relativeX = side === 'left' ? 0 : CARD_WIDTH;
    const relativeY = HEADER_HEIGHT + fieldIndex * ROW_HEIGHT + ROW_HEIGHT / 2;

    return {
      x: pos.x + relativeX,
      y: pos.y + relativeY
    };
  };

  // Drag-and-drop link execution
  const handlePortClick = (table: string, fieldName: string, side: 'left' | 'right') => {
    if (!linkingStart) {
      setLinkingStart({ table, field: fieldName });
    } else {
      if (linkingStart.table === table) {
        // Cancel linking on same table click
        setLinkingStart(null);
        return;
      }
      
      // Prevent duplicate connections
      const exists = connections.some(
        c => (c.fromTable === linkingStart.table && c.fromField === linkingStart.field && c.toTable === table && c.toField === fieldName) ||
             (c.fromTable === table && c.fromField === fieldName && c.toTable === linkingStart.table && c.toField === linkingStart.field)
      );

      if (!exists) {
        setConnections([
          ...connections,
          {
            fromTable: linkingStart.table,
            fromField: linkingStart.field,
            toTable: table,
            toField: fieldName
          }
        ]);
        message.success(`Linked ${linkingStart.table}.${linkingStart.field} with ${table}.${fieldName}`);
      }
      setLinkingStart(null);
    }
  };

  const handleWorkspaceMouseMove = (e: React.MouseEvent) => {
    if (!linkingStart || !workspaceRef.current) return;
    const rect = workspaceRef.current.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom
    });
  };

  const removeConnection = (index: number) => {
    const updated = [...connections];
    updated.splice(index, 1);
    setConnections(updated);
  };

  // Compile visual connection setup and run query
  const executeQuery = async () => {
    if (activeNodes.length === 0) {
      message.error("Please add at least one table node to the canvas");
      return;
    }
    if (isGrouping) {
      if (!groupByField) {
        message.error("Please select a Group By column");
        return;
      }
    } else {
      if (selectedColumns.length === 0) {
        message.error("Please select at least one column output to generate the report");
        return;
      }
    }

    setLoading(true);
    const result = await NodeReportQuery_api(
      activeNodes,
      connections,
      selectedColumns,
      filters.map(f => ({ field: f.field, operator: f.operator, value: f.value })),
      limit,
      isGrouping ? groupByField : "",
      isGrouping && aggField ? { field: aggField, function: aggFunc } : null
    );

    if (result.success) {
      setReportData(result.data || []);
      setSqlDebug(result.debug_sql || "");
      message.success(`Report compiled successfully. Retrieved ${result.rowCount} records.`);
    } else {
      message.error(result.msg || "Failed to execute connector query");
    }
    setLoading(false);
  };

  // Download Report helpers
  const handleExportExcel = () => {
    if (reportData.length === 0) return;
    let headers: string[] = [];
    let formattedData: any[][] = [];

    if (isGrouping) {
      const aggLabel = aggField ? `${aggFunc}(${aggField})` : "Count";
      headers = [groupByField, aggLabel];
      formattedData = reportData.map(row => [
        row[groupByField] ?? row[groupByField.replace('.', '$')] ?? "--",
        row.aggregate_value ?? 0
      ]);
    } else {
      headers = selectedColumns;
      formattedData = reportData.map(row => 
        selectedColumns.map(col => row[col.replace('.', '$')] ?? row[col] ?? "")
      );
    }

    downloadExcel("Visual Connector Custom Report", headers, formattedData, `node_report_${new Date().getTime()}`);
  };

  // Whitelist filtering fields
  const getFilterableFields = () => {
    const fields: { value: string, label: string }[] = [];
    activeNodes.forEach(node => {
      const config = TABLE_NODES_CONFIG[node];
      config.fields.forEach(f => {
        fields.push({
          value: `${node}.${f.name}`,
          label: `[${config.label}] ${f.name}`
        });
      });
    });
    return fields;
  };

  return (
    <div className="w-full h-screen bg-slate-50 text-slate-800 overflow-hidden flex flex-col relative font-sans">
      
      {/* Premium Light Theme Custom CSS Overrides */}
      <style>{`
        /* Ant Design Select Custom Light Styling */
        .select-dark .ant-select-selector {
          background-color: #ffffff !important;
          border-color: #cbd5e1 !important;
          color: #0f172a !important;
          border-radius: 0.5rem !important;
          height: 32px !important;
        }
        .select-dark .ant-select-selection-item {
          color: #0f172a !important;
          font-weight: 500 !important;
          line-height: 30px !important;
        }
        .select-dark .ant-select-arrow {
          color: #64748b !important;
        }
        .node-checkbox .ant-checkbox-inner {
          background-color: #ffffff !important;
          border-color: #cbd5e1 !important;
          border-radius: 4px !important;
        }
        .node-checkbox .ant-checkbox-checked .ant-checkbox-inner {
          background-color: #6366f1 !important;
          border-color: #4f46e5 !important;
        }
        .node-checkbox .ant-checkbox-wrapper:hover .ant-checkbox-inner,
        .node-checkbox .ant-checkbox:hover .ant-checkbox-inner {
          border-color: #818cf8 !important;
        }
        
        /* Premium custom scrollbar stylings */
        .custom-scroll::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .custom-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scroll::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 9999px;
        }
        .custom-scroll::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>

      {/* Visual Workspace Header */}
      <header className="h-16 px-6 border-b border-slate-200 bg-white/80 backdrop-blur-xl flex justify-between items-center z-20 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => navigate('/admin/custom_reports')}
            type="text" 
            className="text-slate-500 hover:text-slate-900 flex items-center gap-2 hover:bg-slate-100 px-3 py-1.5 rounded-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> Standard Reports
          </Button>
          <div className="w-px h-6 bg-slate-200" />
          <h1 className="text-lg font-bold tracking-tight text-slate-800 flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-600 animate-pulse" />
            Visual Connector Report Studio
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 flex items-center gap-1.5 bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
            <span>
              {loadedTemplateName
                ? `Editing: ${loadedTemplateName}`
                : `Workspace Active (${activeNodes.length} Nodes)`}
            </span>
          </div>

          <Button
            onClick={() => setIsErdOpen(true)}
            className="bg-indigo-50 text-indigo-650 border-indigo-200 hover:bg-indigo-100/50 h-10 px-4 rounded-lg flex items-center gap-2 font-medium transition-all"
          >
            <ArrowLeftRight className="w-4 h-4 text-indigo-600" /> Database ERD Guide
          </Button>

          <Button
            onClick={() => setIsAiDrawerOpen(true)}
            className="border-none h-10 px-4 rounded-lg flex items-center gap-2 font-medium transition-all hover:scale-[1.02]"
          >
            <Bot className="w-4 h-4 text-white" /> AI Designer
          </Button>

          <Button
            onClick={executeQuery}
            loading={loading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white border-none h-10 px-5 rounded-lg flex items-center gap-2 font-medium shadow-lg shadow-indigo-600/10 transition-all hover:scale-102"
          >
            <Play className="w-4 h-4" /> Run Query
          </Button>
        </div>
      </header>

      {/* Templates Bar */}
      <div className="h-12 px-6 border-b border-slate-200 bg-slate-50 flex items-center justify-between z-10 flex-shrink-0">
        <div className="flex items-center gap-2 overflow-x-auto custom-scroll py-1 max-w-[70%] whitespace-nowrap">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
            <FolderOpen className="w-3.5 h-3.5 text-slate-500" /> Saved Templates:
          </span>
          {templates.length === 0 ? (
            <span className="text-xs text-slate-400 italic">No saved templates</span>
          ) : (
            templates.map(t => (
              <div 
                key={t.id}
                onClick={() => handleLoadTemplate(t)}
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 px-2.5 py-1 rounded-lg text-xs cursor-pointer text-slate-700 transition-all font-medium group"
              >
                <span>{t.name}</span>
                <Trash2 
                  onClick={(e) => handleDeleteTemplate(e, t.id)}
                  className="w-3.5 h-3.5 text-slate-400 hover:text-rose-600 transition-colors ml-1" 
                />
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-2">
          {loadedTemplateId && (
            <Button
              onClick={handleUpdateTemplate}
              className="bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200 h-8 text-xs rounded-lg flex items-center gap-1.5 font-medium transition-all"
            >
              <Edit3 className="w-3.5 h-3.5 text-amber-600" /> Update "{loadedTemplateName}"
            </Button>
          )}
          <Button
            onClick={() => setIsSaveModalOpen(true)}
            className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200 h-8 text-xs rounded-lg flex items-center gap-1.5 font-medium transition-all"
          >
            <Save className="w-3.5 h-3.5 text-slate-500" /> Save as New
          </Button>
        </div>
      </div>

      {/* Workspace Area split */}
      <div className="flex-1 flex overflow-hidden relative">
        
        {/* Left Toolbar Drawer: Database Nodes Selector */}
        <aside className="w-72 border-r border-slate-200 bg-white/60 backdrop-blur-md flex flex-col z-10 flex-shrink-0">
          <div className="p-4 border-b border-slate-200">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Available Tables</span>
            <Input
              placeholder="Search tables..."
              value={searchTableQuery}
              onChange={(e) => setSearchTableQuery(e.target.value)}
              className="bg-white border-slate-350 text-slate-850 placeholder-slate-400 rounded-lg hover:border-indigo-500 focus:border-indigo-500"
              size="small"
              allowClear
            />
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scroll">
            {Object.keys(TABLE_NODES_CONFIG)
              .filter(key => TABLE_NODES_CONFIG[key].label.toLowerCase().includes(searchTableQuery.toLowerCase()))
              .map(key => {
                const schema = TABLE_NODES_CONFIG[key];
                const added = activeNodes.includes(key);

                return (
                  <button
                    key={key}
                    onClick={() => addNode(key)}
                    disabled={added}
                    className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-center justify-between ${
                      added
                        ? "bg-slate-100 border-slate-200/60 text-slate-400 cursor-not-allowed"
                        : "bg-white border-slate-200 text-slate-700 hover:border-indigo-500 hover:bg-slate-50 hover:translate-x-1"
                    }`}
                  >
                    <div>
                      <div className="font-semibold text-sm">{schema.label}</div>
                      <div className="text-[10px] text-slate-400 uppercase font-mono mt-0.5">{key}</div>
                    </div>
                    {!added && (
                      <Plus className="w-4 h-4 text-indigo-600" />
                    )}
                  </button>
                );
              })}
          </div>
        </aside>

        {/* Dynamic Nodes Canvas Workspace */}
        <main 
          ref={workspaceRef}
          onMouseMove={handleWorkspaceMouseMove}
          className="flex-1 relative overflow-hidden bg-slate-50 bg-[radial-gradient(#cbd5e1_1.5px,transparent_1.5px)] [background-size:32px_32px] w-full h-full"
        >
          {/* Floating Action Zoom Controls Overlay */}
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md border border-slate-200 rounded-xl p-1.5 shadow-lg flex items-center gap-1 z-30 select-none">
            <Tooltip title="Zoom Out">
              <Button 
                size="small" 
                type="text" 
                icon={<ZoomOut className="w-3.5 h-3.5 text-slate-500" />} 
                onClick={() => setZoom(prev => Math.max(prev - 0.1, 0.5))}
              />
            </Tooltip>
            <span className="text-[10px] font-bold text-slate-550 min-w-[36px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <Tooltip title="Zoom In">
              <Button 
                size="small" 
                type="text" 
                icon={<ZoomIn className="w-3.5 h-3.5 text-slate-500" />} 
                onClick={() => setZoom(prev => Math.min(prev + 0.1, 1.5))}
              />
            </Tooltip>
            <div className="w-px h-4 bg-slate-200 mx-1" />
            <Tooltip title="Reset Zoom">
              <Button 
                size="small" 
                type="text" 
                icon={<RotateCcw className="w-3.5 h-3.5 text-slate-500" />} 
                onClick={() => setZoom(1.0)}
              />
            </Tooltip>
          </div>

          {/* Instructions when workspace is empty */}
          {activeNodes.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-slate-400 z-10">
              <Database className="w-16 h-16 text-slate-300 mb-4 animate-bounce" />
              <p className="text-sm font-medium">Your canvas workspace is empty</p>
              <p className="text-xs text-slate-400 mt-1">Select a table node on the left to spawn it</p>
            </div>
          )}

          {/* Zoomable Canvas Wrapper Container */}
          <div 
            className="absolute inset-0 origin-top-left transition-transform duration-105 ease-out"
            style={{ 
              transform: `scale(${zoom})`, 
              width: `${100 / zoom}%`, 
              height: `${100 / zoom}%` 
            }}
          >
            {/* SVG Connector Layer */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
            {/* Draw active established connections */}
            {connections.map((conn, idx) => {
              const start = getPortCoords(conn.fromTable, conn.fromField, 'right');
              const end = getPortCoords(conn.toTable, conn.toField, 'left');
              
              const dx = Math.abs(end.x - start.x) * 0.5;
              const path = `M ${start.x} ${start.y} C ${start.x + dx} ${start.y}, ${end.x - dx} ${end.y}, ${end.x} ${end.y}`;

              return (
                <g key={idx} className="pointer-events-auto cursor-pointer group">
                  <path
                    d={path}
                    fill="none"
                    stroke="transparent"
                    strokeWidth="10"
                    onClick={() => removeConnection(idx)}
                  />
                  <path
                    d={path}
                    fill="none"
                    stroke="#4f46e5"
                    strokeWidth="2.5"
                    className="group-hover:stroke-rose-500 transition-all"
                  />
                  {/* Neon connection glow */}
                  <path
                    d={path}
                    fill="none"
                    stroke="#818cf8"
                    strokeWidth="1.5"
                    opacity="0.3"
                    className="group-hover:stroke-rose-400"
                  />
                  {/* Connection details hover */}
                  <circle cx={(start.x + end.x)/2} cy={(start.y + end.y)/2} r="5" className="fill-indigo-600 opacity-0 group-hover:opacity-100 pointer-events-none transition-all" />
                </g>
              );
            })}

            {/* Draw temporary dragging linking line */}
            {linkingStart && (
              (() => {
                const start = getPortCoords(linkingStart.table, linkingStart.field, 'right');
                const dx = Math.abs(mousePos.x - start.x) * 0.5;
                const path = `M ${start.x} ${start.y} C ${start.x + dx} ${start.y}, ${mousePos.x - dx} ${mousePos.y}, ${mousePos.x} ${mousePos.y}`;

                return (
                  <path
                    d={path}
                    fill="none"
                    stroke="#4f46e5"
                    strokeWidth="2"
                    strokeDasharray="4 4"
                  />
                );
              })()
            )}
          </svg>

          {/* Render Draggable Node Cards */}
          {activeNodes.map((tableKey) => {
            const schema = TABLE_NODES_CONFIG[tableKey];
            const pos = nodePositions[tableKey] || { x: 100, y: 100 };

            return (
              <Draggable
                key={tableKey}
                position={pos}
                scale={zoom}
                onDrag={(e, data) => {
                  setNodePositions(prev => ({
                    ...prev,
                    [tableKey]: { x: data.x, y: data.y }
                  }));
                }}
                bounds="parent"
                handle=".node-handle"
              >
                <div 
                  className="absolute bg-white/95 border border-slate-200 shadow-lg rounded-2xl overflow-hidden z-10 select-none hover:border-slate-355 transition-colors"
                  style={{ width: CARD_WIDTH }}
                >
                  
                  {/* Node Header handle */}
                  <div className="node-handle cursor-grab active:cursor-grabbing p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                    <div>
                      <div className="font-bold text-sm text-slate-800 flex items-center gap-1.5">
                        <Database className="w-3.5 h-3.5 text-indigo-600" />
                        {schema.label}
                      </div>
                      <div className="text-[9px] font-mono text-slate-400 uppercase mt-0.5">{tableKey}</div>
                    </div>
                    <div className="flex items-center gap-1" onMouseDown={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={schema.fields.every(f => selectedColumns.includes(`${tableKey}.${f.name}`))}
                        indeterminate={
                          schema.fields.some(f => selectedColumns.includes(`${tableKey}.${f.name}`)) &&
                          !schema.fields.every(f => selectedColumns.includes(`${tableKey}.${f.name}`))
                        }
                        onChange={(e) => {
                          const fieldKeys = schema.fields.map(f => `${tableKey}.${f.name}`);
                          if (e.target.checked) {
                            const toAdd = fieldKeys.filter(k => !selectedColumns.includes(k));
                            setSelectedColumns([...selectedColumns, ...toAdd]);
                          } else {
                            setSelectedColumns(selectedColumns.filter(k => !fieldKeys.includes(k)));
                          }
                        }}
                        title="Select/Deselect All Columns"
                        className="node-checkbox mr-1"
                      />
                      <button 
                        onClick={() => removeNode(tableKey)}
                        className="p-1 rounded text-slate-400 hover:text-rose-500 hover:bg-slate-100 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Fields list */}
                  <div className="p-1.5 flex flex-col">
                    {schema.fields.map((f) => {
                      const colKey = `${tableKey}.${f.name}`;
                      const isSelected = selectedColumns.includes(colKey);
                      const isLinked = connections.some(c => (c.fromTable === tableKey && c.fromField === f.name) || (c.toTable === tableKey && c.toField === f.name));

                      return (
                        <div 
                          key={f.name} 
                          className="h-9 px-3 flex items-center justify-between hover:bg-slate-100/60 rounded-lg group relative"
                          style={{ height: ROW_HEIGHT }}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedColumns([...selectedColumns, colKey]);
                                } else {
                                  setSelectedColumns(selectedColumns.filter(c => c !== colKey));
                                }
                              }}
                              className="node-checkbox border-slate-300"
                            />
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold text-slate-700 group-hover:text-indigo-650 transition-colors">{f.name}</span>
                              <span className="text-[8px] font-mono text-slate-400 mt-px">{f.type}</span>
                            </div>
                          </div>

                          {/* Connection Port anchor dots */}
                          <div className="flex items-center gap-1.5">
                            {isLinked && (
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-600" />
                            )}
                            <button
                              onClick={() => handlePortClick(tableKey, f.name, 'right')}
                              className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center transition-all ${
                                linkingStart?.table === tableKey && linkingStart?.field === f.name
                                  ? "bg-amber-500 border-amber-400 scale-125 animate-ping"
                                  : "bg-white border-slate-300 hover:bg-indigo-600 hover:border-indigo-500 group-hover:scale-110"
                              }`}
                            >
                              <ChevronRight className="w-2.5 h-2.5 text-slate-400 hover:text-white" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Draggable>
            );
          })}
          </div> {/* End Zoomable Canvas Wrapper Container */}

          {/* HUD Styled SQL debugging console absolute overlay */}
          {sqlDebug && (
            <div className="absolute bottom-4 left-4 max-w-xl bg-white border border-slate-200 px-4 py-2.5 rounded-xl text-[10px] font-mono text-slate-500 z-10 shadow-lg backdrop-blur-md flex items-center gap-2">
              <span className="text-indigo-600 font-semibold uppercase flex-shrink-0">SQL Output:</span>
              <span className="truncate max-w-md select-text">{sqlDebug}</span>
            </div>
          )}

          {/* Overlaid Bottom query results panel absolute inside workspace canvas */}
          {reportData.length > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-72 border-t border-slate-200 bg-white flex flex-col z-20">
              <div className="h-12 px-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <span className="text-xs font-bold text-slate-650 uppercase tracking-widest flex items-center gap-2">
                  <Database className="w-4 h-4 text-indigo-600" /> Query Results ({reportData.length} Rows)
                </span>

                <div className="flex gap-2">
                  <Button
                    onClick={handleExportExcel}
                    className="bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-600 border-none px-3.5 h-8 text-xs rounded-lg flex items-center gap-1.5 transition-all"
                  >
                    <Download className="w-3.5 h-3.5" /> Excel
                  </Button>
                  <button 
                    onClick={() => setReportData([])}
                    className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto max-h-[calc(100vh-320px)] p-4 select-text custom-scroll">
                <table className="w-full min-w-max border-collapse text-left text-xs font-medium text-slate-700">
                  <thead>
                    {isGrouping ? (
                      <tr className="border-b border-slate-200 text-slate-400 uppercase tracking-wider text-[10px]">
                        <th className="pb-2.5 px-4 font-bold">{groupByField}</th>
                        <th className="pb-2.5 px-4 font-bold">{aggField ? `${aggFunc}(${aggField})` : "Count"}</th>
                      </tr>
                    ) : (
                      <tr className="border-b border-slate-200 text-slate-400 uppercase tracking-wider text-[10px]">
                        {selectedColumns.map(col => (
                          <th key={col} className="pb-2.5 px-4 font-bold">{col}</th>
                        ))}
                      </tr>
                    )}
                  </thead>
                  <tbody className="divide-y divide-slate-200/60">
                    {isGrouping ? (
                      reportData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/80">
                          <td className="py-2.5 px-4 font-medium max-w-xs truncate border-b border-slate-100">
                            {row[groupByField] ?? row[groupByField.replace('.', '$')] ?? "--"}
                          </td>
                          <td className="py-2.5 px-4 font-medium max-w-xs truncate border-b border-slate-100">
                            {row.aggregate_value ?? 0}
                          </td>
                        </tr>
                      ))
                    ) : (
                      reportData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/80">
                          {selectedColumns.map(col => {
                            const key = col.replace('.', '$');
                            return (
                              <td key={col} className="py-2.5 px-4 font-medium max-w-xs truncate border-b border-slate-100">
                                {row[key] ?? row[col] ?? "--"}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>

        {/* Right drawer: Dynamic Query Controls & Filters */}
        <aside className="w-80 border-l border-slate-200 bg-white/60 backdrop-blur-md flex flex-col z-10 flex-shrink-0">
          <div className="p-4 border-b border-slate-200">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 text-indigo-600" /> Filter Criteria
            </span>
          </div>

          {/* Applied filters lists */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scroll">
            {filters.length === 0 && (
              <div className="text-center py-8 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                <span className="text-xs text-slate-400 font-medium italic">No filters mapped</span>
              </div>
            )}
            
            {filters.map((filter, index) => (
              <div key={filter.id} className="p-3 bg-slate-100 border border-slate-200 rounded-xl relative group">
                <button
                  onClick={() => setFilters(filters.filter(f => f.id !== filter.id))}
                  className="absolute -top-2 -right-2 bg-white shadow-md border border-slate-200 rounded-full p-1 text-slate-400 hover:text-rose-500 hover:border-rose-550 transition-all opacity-0 group-hover:opacity-100"
                >
                  <X className="w-3 h-3" />
                </button>
                
                <div className="space-y-2">
                  <Select
                    value={filter.field}
                    onChange={(v) => {
                      const updated = [...filters];
                      updated[index].field = v;
                      setFilters(updated);
                    }}
                    className="w-full select-dark"
                    size="small"
                  >
                    {getFilterableFields().map(ff => (
                      <Option key={ff.value} value={ff.value}>{ff.label}</Option>
                    ))}
                  </Select>

                  <div className="grid grid-cols-2 gap-1.5">
                    <Select
                      value={filter.operator}
                      onChange={(v) => {
                        const updated = [...filters];
                        updated[index].operator = v;
                        setFilters(updated);
                      }}
                      className="w-full select-dark"
                      size="small"
                    >
                      <Option value="=">Equals</Option>
                      <Option value="contains">Contains</Option>
                      <Option value=">">Greater Than</Option>
                      <Option value="<">Less Than</Option>
                    </Select>

                    <Input
                      placeholder="Value"
                      value={filter.value}
                      onChange={(e) => {
                        const updated = [...filters];
                        updated[index].value = e.target.value;
                        setFilters(updated);
                      }}
                      className="bg-white border-slate-300 text-slate-800 placeholder-slate-400 text-xs rounded-lg"
                      size="small"
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              onClick={() => {
                const fables = getFilterableFields();
                if (fables.length === 0) return;
                setFilters([
                  ...filters,
                  {
                    id: Math.random().toString(),
                    field: fables[0].value,
                    operator: "=",
                    value: ""
                  }
                ]);
              }}
              disabled={activeNodes.length === 0}
              className="w-full bg-white border-slate-200 text-indigo-600 hover:text-indigo-700 hover:border-indigo-500 hover:bg-slate-50 rounded-xl"
            >
              <PlusCircle className="w-4 h-4 mr-2" /> Add Filter
            </Button>
          </div>

          {/* Aggregations / Group By panel */}
          <div className="p-4 border-t border-slate-200 bg-slate-50/50 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <PieChart className="w-3.5 h-3.5 text-indigo-650" /> Group & Summarize
              </span>
              <Checkbox 
                checked={isGrouping} 
                onChange={(e) => setIsGrouping(e.target.checked)}
                className="node-checkbox"
              />
            </div>

            {isGrouping && (
              <div className="space-y-2">
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Group By Column</span>
                  <Select
                    value={groupByField || undefined}
                    onChange={setGroupByField}
                    placeholder="Select Grouping Column"
                    className="w-full select-dark"
                    size="small"
                  >
                    {getFilterableFields().map(ff => (
                      <Option key={ff.value} value={ff.value}>{ff.label}</Option>
                    ))}
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Calculation</span>
                    <Select
                      value={aggFunc}
                      onChange={setAggFunc}
                      className="w-full select-dark"
                      size="small"
                    >
                      <Option value="COUNT">Count</Option>
                      <Option value="SUM">Sum</Option>
                      <Option value="AVG">Average</Option>
                      <Option value="MIN">Minimum</Option>
                      <Option value="MAX">Maximum</Option>
                    </Select>
                  </div>

                  <div>
                    <span className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Metric Column</span>
                    <Select
                      value={aggField || undefined}
                      onChange={setAggField}
                      placeholder="Select Metric"
                      className="w-full select-dark"
                      size="small"
                      allowClear
                    >
                      {getFilterableFields().map(ff => (
                        <Option key={ff.value} value={ff.value}>{ff.label}</Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Row limit panel */}
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Max Row Limit</span>
            <Select
              value={limit}
              onChange={setLimit}
              className="w-full select-dark"
              size="small"
            >
              <Option value={100}>100 rows</Option>
              <Option value={500}>500 rows</Option>
              <Option value={1000}>1000 rows</Option>
            </Select>
          </div>
        </aside>
      </div>

      {/* Save Template Modal */}
      <Modal
        title="Save Report Template"
        open={isSaveModalOpen}
        onOk={handleSaveTemplate}
        onCancel={() => setIsSaveModalOpen(false)}
        okText="Save"
        cancelText="Cancel"
      >
        <div className="py-4">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
            Template Name
          </label>
          <Input 
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="E.g. Dealer Stock Summary, Customer Totals"
          />
        </div>
      </Modal>

      {/* Database ERD Guide Modal */}
      <Modal
        title={
          <span className="text-slate-800 font-bold flex items-center gap-2 text-base">
            <ArrowLeftRight className="w-5 h-5 text-indigo-650" /> Database Relationship Map (ERD)
          </span>
        }
        open={isErdOpen}
        onCancel={() => setIsErdOpen(false)}
        footer={null}
        width={680}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <div className="py-2 space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            This reference guide maps all active foreign-key constraints in the database. 
            Click <strong className="text-indigo-650">"Auto-Join Nodes"</strong> to instantly add both table nodes to your workspace canvas and link them.
          </p>
          
          <Input
            placeholder="Search tables or descriptions..."
            value={erdSearch}
            onChange={(e) => setErdSearch(e.target.value)}
            className="w-full bg-slate-50 border-slate-200 text-slate-800 hover:border-indigo-500 focus:border-indigo-500 rounded-lg"
            allowClear
          />

          <div className="space-y-2.5 max-h-[45vh] overflow-y-auto pr-1 custom-scroll">
            {RELATION_MAP.filter(
              r =>
                r.fromTable.toLowerCase().includes(erdSearch.toLowerCase()) ||
                r.toTable.toLowerCase().includes(erdSearch.toLowerCase()) ||
                r.description.toLowerCase().includes(erdSearch.toLowerCase())
            ).map((rel, idx) => {
              const isMapped = connections.some(
                c =>
                  (c.fromTable === rel.fromTable && c.fromField === rel.fromField && c.toTable === rel.toTable && c.toField === rel.toField) ||
                  (c.fromTable === rel.toTable && c.fromField === rel.toField && c.toTable === rel.fromTable && c.toField === rel.fromField)
              );

              return (
                <div 
                  key={idx} 
                  className={`p-3.5 border rounded-xl flex justify-between items-center transition-all ${
                    isMapped 
                      ? "bg-indigo-50/20 border-indigo-200" 
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded text-[11px] font-mono font-bold">
                        {rel.fromTable}.{rel.fromField}
                      </span>
                      <span className="text-slate-400 font-bold text-xs">↔</span>
                      <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded text-[11px] font-mono font-bold">
                        {rel.toTable}.{rel.toField}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 font-medium italic mt-1">{rel.description}</p>
                  </div>

                  <Button
                    onClick={() => autoJoinRelation(rel)}
                    type={isMapped ? "text" : "default"}
                    disabled={isMapped}
                    className={
                      isMapped
                        ? "text-emerald-600 hover:text-emerald-700 font-semibold text-xs border-none cursor-default"
                        : "bg-indigo-50 hover:bg-indigo-100/85 text-indigo-650 border-indigo-200 text-xs font-semibold rounded-lg"
                    }
                  >
                    {isMapped ? "Joined ✓" : "Auto-Join Nodes"}
                  </Button>
                </div>
              );
            })}

            {RELATION_MAP.filter(
              r =>
                r.fromTable.toLowerCase().includes(erdSearch.toLowerCase()) ||
                r.toTable.toLowerCase().includes(erdSearch.toLowerCase()) ||
                r.description.toLowerCase().includes(erdSearch.toLowerCase())
            ).length === 0 && (
              <div className="text-center py-8 text-slate-400 text-xs italic">
                No database relationships found matching "{erdSearch}"
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* AI Workspace Designer Drawer */}
      <Drawer
        title={
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Bot className="w-5 h-5 text-indigo-650" />
            <span className="font-bold text-slate-800 text-base">AI Studio Designer</span>
          </div>
        }
        open={isAiDrawerOpen}
        onClose={() => {
          setIsAiDrawerOpen(false);
          setAiPrompt("");
        }}
        width={500}
      >
        <div className="py-1 flex flex-col h-full gap-4">
          <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100 flex-shrink-0">
            <h4 className="text-xs font-bold text-indigo-800 uppercase tracking-wider mb-1">Conversational Workspace Builder</h4>
            <p className="text-xs text-slate-650 leading-relaxed font-medium">
              Interact with the AI agent to dynamically construct and update your workspace report. You can say <i>"Build a workspace for inquiries"</i>, then follow up with <i>"Add dealers and connect them"</i> or <i>"Filter by amount greater than 5000"</i>.
            </p>
          </div>

          {/* Chat Feed */}
          <div className="flex-1 overflow-y-auto pr-1 space-y-4 custom-scroll py-2 border border-slate-100 rounded-xl p-3 bg-slate-50/30 animate-fade-in" style={{ minHeight: '300px', maxHeight: '50vh' }}>
            {chatHistory.length === 0 ? (
              <div className="text-center py-16 text-slate-400 text-xs italic flex flex-col items-center justify-center gap-2">
                <Sparkles className="w-8 h-8 text-indigo-400 opacity-60 animate-pulse" />
                No message history yet. Describe your report to begin!
              </div>
            ) : (
              chatHistory.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] rounded-2xl p-3 text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-tr-none shadow-md'
                      : 'bg-white text-slate-850 rounded-tl-none border border-slate-200 shadow-sm'
                  }`}>
                    <div className="font-bold mb-0.5 opacity-80 flex items-center gap-1">
                      {msg.role === 'user' ? (
                        'User Request'
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3 text-indigo-600 animate-spin" style={{ animationDuration: '3s' }} />
                          AI Analyst Response
                        </>
                      )}
                    </div>
                    <div className="whitespace-pre-wrap select-text font-medium">{msg.text}</div>
                  </div>
                </div>
              ))
            )}
            
            {aiLoading && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-tl-none p-3.5 text-xs bg-white text-slate-800 border border-indigo-250 shadow-sm flex items-center gap-3 animate-pulse">
                  <Spin size="small" />
                  <span className="text-slate-500 font-medium italic">AI is parsing relations and mapping canvas...</span>
                </div>
              </div>
            )}
          </div>

          {/* Chat Input controls */}
          <div className="flex flex-col gap-2 mt-auto border-t border-slate-150 pt-3 flex-shrink-0">
            <div className="flex gap-2">
              <TextArea
                placeholder="Type your design instructions..."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                disabled={aiLoading}
                rows={2}
                className="rounded-lg text-xs border-slate-200 resize-none flex-1 font-medium text-slate-800"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (aiPrompt.trim() && !aiLoading) {
                      handleAiDesign(aiPrompt);
                    }
                  }
                }}
              />
              <Button
                onClick={() => handleAiDesign(aiPrompt)}
                disabled={aiLoading || !aiPrompt.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white border-none h-auto px-4 rounded-lg flex items-center justify-center font-bold"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex justify-between items-center text-[10px] text-slate-400 font-bold px-1 select-none">
              <span>Press Enter to send</span>
              {chatHistory.length > 0 && (
                <button 
                  onClick={() => {
                    setChatHistory([]);
                    message.success("Chat history cleared");
                  }}
                  className="text-rose-500 hover:text-rose-600 hover:underline font-bold"
                >
                  Clear Chat History
                </button>
              )}
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};
